import React, { useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";

import ImageOutlinedIcon from "@material-ui/icons/ImageOutlined";
import VideocamOutlinedIcon from "@material-ui/icons/VideocamOutlined";
import InsertDriveFileOutlinedIcon from "@material-ui/icons/InsertDriveFileOutlined";
import ReplyOutlinedIcon from "@material-ui/icons/ReplyOutlined";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import PhoneIcon from "@material-ui/icons/Phone";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import ListIcon from "@material-ui/icons/List";
import SignalCellular4BarIcon from "@material-ui/icons/SignalCellular4Bar";
import WifiIcon from "@material-ui/icons/Wifi";
import BatteryFullIcon from "@material-ui/icons/BatteryFull";

import { i18n } from "../../translate/i18n";
import { getTokens } from "../../styles/tokens";
import { applyExamples, extractVariables } from "../../pages/MetaTemplates/templateUtils";
import { formatWhatsapp } from "./formatWhatsapp";

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  // Da tela para dentro as cores são as do WhatsApp (é uma imitação do app);
  // a moldura em volta segue os tokens para não sumir no tema escuro.
  return {
    phone: {
      width: 300,
      maxWidth: "100%",
      margin: "0 auto",
      background: isDark ? "#05080f" : "#141b2b",
      border: `1px solid ${t.lineStrong}`,
      borderRadius: 30,
      padding: 8,
      boxShadow: t.shadow.soft,
      fontFamily: t.font,
    },
    screen: {
      borderRadius: 22,
      overflow: "hidden",
      background: isDark ? "#0b141a" : "#e5ddd5",
      backgroundImage: isDark
        ? "radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)"
        : "radial-gradient(rgba(0,0,0,0.05) 1px, transparent 1px)",
      backgroundSize: "18px 18px",
      display: "flex",
      flexDirection: "column",
      minHeight: 470,
    },
    statusBar: {
      height: 22,
      background: isDark ? "#1f2c34" : "#075e54",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "0 16px",
      color: "#fff",
      fontSize: 10,
      fontWeight: 600,
    },
    statusIcons: {
      display: "inline-flex",
      alignItems: "center",
      gap: 3,
      "& svg": { fontSize: 11 },
    },
    topBar: {
      background: isDark ? "#1f2c34" : "#075e54",
      color: "#fff",
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "8px 12px 10px",
    },
    avatar: {
      width: 30,
      height: 30,
      borderRadius: "50%",
      background: "rgba(255,255,255,0.25)",
      flexShrink: 0,
    },
    businessName: {
      fontSize: 13,
      fontWeight: 600,
      lineHeight: 1.2,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    businessSub: { fontSize: 10, opacity: 0.8 },
    chat: {
      flex: 1,
      padding: "14px 10px",
      display: "flex",
      flexDirection: "column",
    },
    bubble: {
      alignSelf: "flex-start",
      maxWidth: "92%",
      minWidth: 120,
      background: isDark ? "#202c33" : "#ffffff",
      color: isDark ? "#e9edef" : "#111b21",
      borderRadius: 8,
      borderTopLeftRadius: 0,
      boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
      fontSize: 13.5,
      lineHeight: 1.4,
      overflow: "hidden",
      wordBreak: "break-word",
    },
    bubbleInner: { padding: "6px 8px 4px" },
    headerText: { fontWeight: 700, fontSize: 14, marginBottom: 4 },
    mediaBox: {
      height: 140,
      background: isDark ? "#2a3942" : "#d1d7db",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: isDark ? "#8696a0" : "#54656f",
      "& svg": { fontSize: 44 },
    },
    mediaImg: {
      display: "block",
      width: "100%",
      height: 140,
      objectFit: "cover",
    },
    docRow: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      background: isDark ? "#1a262d" : "#f5f6f6",
      padding: "8px 10px",
      margin: "4px 4px 0",
      borderRadius: 6,
      fontSize: 12.5,
      "& svg": { color: "#e53935", fontSize: 28 },
    },
    docName: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      minWidth: 0,
    },
    body: { whiteSpace: "pre-wrap" },
    variable: {
      background: isDark ? "rgba(83,189,235,0.18)" : "rgba(2,126,181,0.12)",
      color: isDark ? "#53bdeb" : "#027eb5",
      borderRadius: 4,
      padding: "0 3px",
      fontFamily: "monospace",
      fontSize: 12,
    },
    footer: {
      color: isDark ? "#8696a0" : "#667781",
      fontSize: 12,
      marginTop: 4,
    },
    time: {
      textAlign: "right",
      color: isDark ? "#8696a0" : "#667781",
      fontSize: 10.5,
      marginTop: 2,
    },
    buttons: { borderTop: `1px solid ${isDark ? "#2f3b43" : "#e9edef"}` },
    button: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      padding: "9px 8px",
      color: isDark ? "#53bdeb" : "#027eb5",
      fontSize: 13.5,
      fontWeight: 500,
      borderTop: `1px solid ${isDark ? "#2f3b43" : "#e9edef"}`,
      "&:first-child": { borderTop: "none" },
      "& svg": { fontSize: 17 },
    },
    placeholder: {
      alignSelf: "center",
      marginTop: "auto",
      marginBottom: "auto",
      color: isDark ? "#8696a0" : "#54656f",
      fontSize: 12.5,
      textAlign: "center",
      padding: 16,
      background: isDark ? "rgba(32,44,51,0.9)" : "rgba(255,255,255,0.85)",
      borderRadius: 8,
    },
  };
});

const BUTTON_ICONS = {
  QUICK_REPLY: ReplyOutlinedIcon,
  URL: OpenInNewIcon,
  PHONE_NUMBER: PhoneIcon,
  COPY_CODE: FileCopyOutlinedIcon,
};

const fileNameFromUrl = (url) => {
  try {
    const path = new URL(url).pathname;
    const last = path.split("/").filter(Boolean).pop();
    return last || "";
  } catch (e) {
    return "";
  }
};

/**
 * Moldura de celular com a mensagem renderizada como no WhatsApp, atualizada
 * em tempo real a partir do estado do formulário.
 */
const PhonePreview = ({ form, businessName }) => {
  const classes = useStyles();
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setImgError(false);
  }, [form.headerExample]);

  const headerVar = extractVariables(form.headerText)[0];
  const headerText = useMemo(
    () =>
      applyExamples(
        form.headerText,
        headerVar ? { [headerVar]: form.headerVarExample } : {}
      ),
    [form.headerText, form.headerVarExample, headerVar]
  );
  const bodyText = useMemo(
    () => applyExamples(form.bodyText, form.bodyExamples),
    [form.bodyText, form.bodyExamples]
  );

  const hasContent =
    form.headerFormat !== "NONE" ||
    !!form.bodyText ||
    !!form.footerText ||
    (form.buttons && form.buttons.length > 0);

  const buttons = Array.isArray(form.buttons) ? form.buttons : [];
  const visibleButtons = buttons.length > 3 ? buttons.slice(0, 2) : buttons;
  const showSeeAll = buttons.length > 3;

  const buttonLabel = (b) => {
    if (b.type === "COPY_CODE") return i18n.t("metaTemplates.preview.copyCode");
    return b.text || i18n.t("metaTemplates.preview.buttonPlaceholder");
  };

  const renderHeader = () => {
    switch (form.headerFormat) {
      case "TEXT":
        return headerText ? (
          <div className={classes.bubbleInner}>
            <div className={classes.headerText}>
              {formatWhatsapp(headerText, classes.variable)}
            </div>
          </div>
        ) : null;
      case "IMAGE":
        return form.headerExample && !imgError ? (
          <img
            src={form.headerExample}
            alt=""
            className={classes.mediaImg}
            onError={() => setImgError(true)}
          />
        ) : (
          <div className={classes.mediaBox}>
            <ImageOutlinedIcon />
          </div>
        );
      case "VIDEO":
        return (
          <div className={classes.mediaBox}>
            <VideocamOutlinedIcon />
          </div>
        );
      case "DOCUMENT":
        return (
          <div className={classes.docRow}>
            <InsertDriveFileOutlinedIcon />
            <span className={classes.docName}>
              {fileNameFromUrl(form.headerExample) ||
                i18n.t("metaTemplates.preview.document")}
            </span>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={classes.phone}>
      <div className={classes.screen}>
        <div className={classes.statusBar}>
          <span>10:30</span>
          <span className={classes.statusIcons} aria-hidden="true">
            <SignalCellular4BarIcon />
            <WifiIcon />
            <BatteryFullIcon />
          </span>
        </div>
        <div className={classes.topBar}>
          <div className={classes.avatar} />
          <div style={{ minWidth: 0 }}>
            <Typography component="div" className={classes.businessName}>
              {businessName || i18n.t("metaTemplates.preview.business")}
            </Typography>
            <Typography component="div" className={classes.businessSub}>
              {i18n.t("metaTemplates.preview.businessAccount")}
            </Typography>
          </div>
        </div>
        <div className={classes.chat}>
          {!hasContent ? (
            <div className={classes.placeholder}>
              {i18n.t("metaTemplates.preview.empty")}
            </div>
          ) : (
            <div className={classes.bubble}>
              {renderHeader()}
              <div className={classes.bubbleInner}>
                {bodyText && (
                  <div className={classes.body}>
                    {formatWhatsapp(bodyText, classes.variable)}
                  </div>
                )}
                {form.footerText && (
                  <div className={classes.footer}>{form.footerText}</div>
                )}
                <div className={classes.time}>10:30</div>
              </div>
              {buttons.length > 0 && (
                <div className={classes.buttons}>
                  {visibleButtons.map((b, idx) => {
                    const Icon = BUTTON_ICONS[b.type] || ReplyOutlinedIcon;
                    return (
                      <div key={idx} className={classes.button}>
                        <Icon />
                        <span>{buttonLabel(b)}</span>
                      </div>
                    );
                  })}
                  {showSeeAll && (
                    <div className={classes.button}>
                      <ListIcon />
                      <span>{i18n.t("metaTemplates.preview.seeAll")}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhonePreview;
